<template>
  <div v-if="section.id == 'proj1sect1'">
    <div v-if="isLeft(side)">
      <row-page-header>
        <span slot="title">IKEA SSFA Dashboard</span>
        <span slot="category">UX / UI Concept</span>
      </row-page-header>
      <div class="row">
        <div class="col-12">
          <p>
            With thousands of products and customers moving through the store daily, how can leaders and their teams more effectively keep track of commercial performance goals in the Self Serve Furniture Area (SSFA) at IKEA?
          </p>
          <p>
            One SSFA manager in Coquitlam has taken the challenge to address this question with his idea of a desktop-based dashboard tool which compiles the KPIs of 3 main commercial areas: inspiration, profitability, and efficiency. This project conceptualizes the idea of a helpful desktop application - <strong>by aggregating multiple logistical and financial KPIs into one central, user-friendly tool, stakeholders can more efficiently and effectively steer the SSFA toward performance goals.</strong>
          </p>
        </div>
      </div>
    </div>
    <div v-if="isRight(side)">
      <row-vertical-center>
        <div class="row" :style="fakeContainerStyle()">
          <div class="col-6" v-for="(icons, index) in firstSectionIcons">
            <card :contents="icons"></card>
          </div>
        </div>
      </row-vertical-center>
    </div>
  </div>
  <div v-else-if="section.id == 'proj1sect2'">
    <row-vertical-center>
      <div class="row bg-color-white">
        <div class="col-12 col-md-8 offset-md-2 text-center">
          <img class="img pt-5 pb-5 w-100" src="@/../static/Interface-Example.png" alt="interface example">
        </div>
      </div>
    </row-vertical-center>
    <div class="row section-height">
      <div class="col-12">
        <div class="row" :style="fakeContainerStyle()">
          <div class="col-12">
            <row-section-header>
              <span slot="title">
                The 3 Categories of "Community"
              </span>
            </row-section-header>
          </div>
        </div>
        <div class="row" :style="fakeContainerStyle()">
          <div class="col-12 col-md-4 mb-5" v-for="(card, index) in commercialityCards">
            <card :contents="card"></card>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div v-else-if="section.id == 'proj1sect3'">
    <div v-if="isLeft(side)" class="mx-auto">
      <row-section-header>
        <span slot="title">
          Challenges &amp; Constraints
        </span>
      </row-section-header>
      <div class="row">
        <div class="col-12">
          <p>
            In a perfect world, the multiple KPIs of the SSFA would be linked and updated based on a system that pulls data automatically from various programs used by the business.
          </p>
          <p>
            However, in the real world of budgets and technological timelines, the current IT situation at IKEA does not allow for this level of automation to occur. Therefore, the project had 3 main constraints:
          </p>
        </div>
      </div>
    </div>
    <div v-if="isRight(side)">
      <div class="row section-height v-align-wrapper mb-5" v-for="constraint in constraints" :style="fakeContainerStyle()">
        <div class="col-12 v-align">
          <h4 class="serif text-center mt-5 py-5">{{ constraint.title }}</h4>
          <p>
            {{ constraint.body }}<strong>{{ constraint.bodyStrong }}</strong>
          </p>
          <div class="row pt-5">
            <div class="col-12">
              <async-animation :async-anim-props="{ name: constraint.animName, delay: 3000, loop: true, onComplete: 'loop' }"></async-animation>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div v-else-if="section.id == 'proj1sect4'">
    <div v-if="isLeft(side)" class="mx-auto" width="100%">
      <row-section-header>
        <span slot="title">
          Original Experience
        </span>
      </row-section-header>
      <div class="row">
        <div class="col-12">
          <p>
            Using the research gathered, I created a series of tasks in order to understand how users are currently operating the SSFA; with the assumptions and constraints in mind, the tasks were structured to follow a pattern of finding the KPIs, understanding the KPI information, and sharing findings among team members. I then ran through this task list with the Assistant Manager, observing his experience as he ran through the set of tasks.
          </p>
        </div>
      </div>
    </div>
    <div v-if="isRight(side)">
      <row-vertical-center>
        <div class="row" :style="fakeContainerStyle()">
          <div class="col-12 mx-auto" v-for="quote in experienceQuotes">
            <blockquote class="blockquote serif">
              <p class="pt-5 pb-3 text-center">
                “{{ quote }}”
              </p>
            </blockquote>
          </div>
        </div>
      </row-vertical-center>
      <!-- for mobile START -->
      <div class="d-block d-md-none">
        <div class="img-overflow y-hidden" style="margin-left: -15px; margin-right: -15px;">
          <img class="img w-100" src="@/../static/Observation.jpg" alt="observation">
        </div>
      </div>
      <!-- for mobile END -->
      <!-- for desktop START -->
      <div class="d-none d-md-block">
        <row-vertical-center>
          <div class="img-overflow y-hidden x-hidden section-height" :style="affixComponentStyle(1)">
            <img class="img h-100 img-h-100" src="@/../static/Observation.jpg" alt="observation">
          </div>
        </row-vertical-center>
      </div>
      <!-- for desktop END -->
    </div>
  </div>
  <div v-else-if="section.id == 'proj1sect5'">
    <div v-if="isLeft(side)" class="mx-auto" width="100%">
      <row-section-header>
        <span slot="title">
          Mapping Out Pain Points
        </span>
      </row-section-header>
      <div class="row">
        <div class="col-12">
          <p>
            I laid out the experience <strong>there were 6 main pain points that I began to address and extracted the project’s goals from them.</strong>
          </p>
        </div>
      </div>
      <div class="row">
        <div class="col-12 col-md-10 offset-md-1">
          <img class="img w-100 mx-auto" src="@/../static/AffinityDiagram.jpg" alt="Affinity Diagram">
        </div>
      </div>
    </div>
    <div v-if="isRight(side)">
      <row-vertical-center>
        <div :style="fakeContainerStyle()">
          <div class="table-responsive">
            <table class="table mb-5">
              <thead>
                <tr>
                  <th scope="col" class="border-top-0">Pain Point</th>
                  <th scope="col" class="border-top-0">Goal</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="painPoint in painPoints">
                  <td class="w-50">{{ painPoint.point }}</td>
                  <td class="w-50">
                    <strong>
                      {{ painPoint.goal }}
                    </strong>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </row-vertical-center>
    </div>
  </div>
  <div v-else-if="section.id == 'proj1sect6'">
    <div v-if="isLeft(side)" class="mx-auto">
      <row-section-header>
        <span slot="title">
          Ideation &amp; Sketching
        </span>
      </row-section-header>
      <div class="row mb-5">
        <div class="col-12">
          <p>
            During the sketching and ideation process it became apparent the hierarchy of displaying data was going to be a challenge.
          </p>
          <p>
            The three main categories (Inspiration, Profitability, Efficiency) housed multiple KPIs, each with one or more metrics that influenced that KPI. Furthermore, each of these metrics would have to be entered in a certain format or style. Thus when designing, we began organizing the hierarchy of data as well as the various input methods necessary for each KPI.
          </p>
        </div>
      </div>
    </div>
    <div v-if="isRight(side)">
      <!-- for mobile START -->
      <div class="d-block d-md-none img-overflow y-hidden" style="margin-left: -15px; margin-right: -15px;">
        <img class="img w-100" src="@/../static/Storyboard.jpg" alt="Storyboard">
      </div>
      <!-- for mobile END -->
      <!-- for desktop START -->
      <div class="d-none d-md-block">
        <row-vertical-center>
          <div class="img-overflow y-hidden x-hidden section-height" :style="affixComponentStyle(1)">
            <img class="img w-100 img-h-100" src="@/../static/Storyboard.jpg" alt="Storyboard">
          </div>
        </row-vertical-center>
      </div>
      <!-- for desktop END -->

      <!-- Original plan
      <div class="row section-height v-align-wrapper" v-for="(index) in [1,2,3]">
        <div class="col-10 offset-1 v-align">
          <img class="img w-100" src="@/../static/Storyboard.jpg" alt="Storyboard">
        </div>
      </div>
      -->
    </div>
  </div>
  <div v-else-if="section.id == 'proj1sect7'">
    <div v-if="isLeft(side)" class="mx-auto" width="100%">
      <row-section-header>
        <span slot="title">
          Lo Fi Prototyping
        </span>
      </row-section-header>
      <div class="row mb-5">
        <div class="col-12">
          <p>
            I also came to a crossroads when considering the order of entering the information. I decided on a card system for the dashboard that would allow the following benefits:
          </p>
        </div>
      </div>
      <div class="row mb-5">
        <div class="col-12 col-md-6" v-for="(text, idx) in lofiPoints">
          <row-column-header>
            <span slot="title">
              {{ idx + 1 }}
            </span>
            <span slot="body">
              {{ text }}
            </span>
          </row-column-header>
        </div>
      </div>
    </div>
    <div v-if="isRight(side)">
      <row-vertical-center>
        <div v-for="prototype in prototypes">
          <div class="row" :style="fakeContainerStyle()">
            <div class="col-12 col-md-6" v-for="col in prototype">
                <row-column-header>
                  <span slot="title">
                    {{ col.title }}
                  </span>
                  <span slot="body">
                    {{ col.body }}
                  </span>
                </row-column-header>
            </div>
          </div>
        </div>
      </row-vertical-center>
    </div>
  </div>
  <div v-else-if="section.id == 'proj1sect9'">
    <div class="row section-height" :style="fakeContainerStyle()">
      <div class="col-12 col-sm-6">
        <row-section-header>
          <span slot="title">
            Dialog Box Layout
          </span>
        </row-section-header>
        <div class="px-5">
          <p>
            When clicking on an individual KPI, an important dialog box appears. As the biggest constraint of the project is the input of information, creating an intuitive way to handle data entry was paramount.
          </p>
          <p>
            The paper prototyping outlined above proved quickly that having the information available for reference was more important than the speed of entering metrics directly. WIthout the information, the user became overwhelmed with trying to retain details of the KPI, slowing down the entry process overall. <strong>By having the update function and access to helpful information side by side, the user can quickly refer to one while utilizing the other.</strong>
          </p>
        </div>
      </div>
      <!-- for mobile START -->
      <div class="col-12 text-center d-block d-md-none">
        <div class="section-height">
          <div class="row">
            <div class="col-12 col-md-6" v-for="wireframe in KPIwireframes">
              <img class="img w-100 pt-3 pb-3" :src="['@/../static/' + wireframe]" alt="wireframe">
            </div>
          </div>
        </div>
      </div>
      <!-- for mobile END -->
      <!-- for desktop START -->
      <div class="col-md-6 text-center d-none d-md-block">
        <div class="section-height v-align-wrapper">
          <div class="v-align">
            <div class="row">
              <div class="col-12 col-md-6" v-for="wireframe in KPIwireframes">
                <img class="img w-100 pt-3 pb-3" :src="['@/../static/' + wireframe]" alt="wireframe">
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- for desktop END -->
    </div>
  </div>
  <div v-else-if="section.id == 'proj1sect10'">
    <div class="row section-height" :style="fakeContainerStyle()">
      <div class="col-12 col-sm-6">
        <row-section-header>
          <span slot="title">
            Dialog Box Goals &amp; Design
          </span>
        </row-section-header>
        <div class="row">
          <div class="col-12" v-for="(goal, idx) in goalsAndDesign">
            <div class="px-5">
              <row-column-header>
                <span slot="title">
                  {{ idx + 1 }}
                </span>
                <span slot="body">
                  {{ goal }}
                </span>
              </row-column-header>
            </div>
          </div>
        </div>
      </div>
      <div class="col-12 col-md-6 section-height v-align-wrapper">
        <div class="v-align">
          <!-- TODO Error: You're using a 3d camera. Try the html renderer.-->
          <!-- <async-animation :async-anim-props="{ name: 'modalsAnim', delay: 0 }"></async-animation> -->
          <img class="img w-100 pt-5 pb-5" src="@/../static/Modalsplaceholder0.png" alt="modals">
        </div>
      </div>
    </div>
  </div>
  <div v-else-if="section.id == 'proj1sect11'">
    <row-vertical-center>
      <div class="row">
        <div class="col-12 col-md-10 offset-md-1 text-center">
          <img class="img w-100" src="@/../static/Dashboard-Main.png" alt="Dashboard Main">
        </div>
        <div class="col-12 col-md-4 offset-md-7">
          <!-- for mobile START -->
          <div class="d-block d-md-none text-center bg-color-white p-4 mt-5">
            <h4 class="serif">Main Overview</h4>
            <p>
              To reduce visual clutter while giving a full overview of the SSFA, the KPIs are reduced to their title and status. The top KPI can be shown in a more detailed view.
            </p>
          </div>
          <!-- for mobile END -->
          <!-- for desktop START -->
          <div class="d-none d-md-block" style="position: relative;">
            <div style="position: absolute; right: 0; top: -150px" class="text-center bg-color-white p-4">
              <h4 class="serif">Main Overview</h4>
              <p>
                To reduce visual clutter while giving a full overview of the SSFA, the KPIs are reduced to their title and status. The top KPI can be shown in a more detailed view.
              </p>
            </div>
          </div>
          <!-- for desktop END -->
        </div>
      </div>
    </row-vertical-center>
  </div>
  <div v-else-if="section.id == 'proj1sect13'">
    <row-vertical-center>
      <div class="row">
        <div class="col-12 col-md-10 offset-md-1 text-center">
          <img class="img w-100" src="@/../static/Dashboard-Category.png" alt="Dashboard Category">
        </div>
        <div class="col-12 col-md-4 offset-md-7">
          <!-- for mobile START -->
          <div class="d-block d-md-none text-center bg-color-white p-4 mt-5">
            <h4 class="serif">Commerciality Category Tab</h4>
            <p>
              Filtering each category gives a full and more detailed view of KPIs in that group, allowing the user to easily focus their efforts on understanding the health of the SSFA in relation to their store’s goals.
            </p>
          </div>
          <!-- for mobile END -->
          <!-- for desktop START -->
          <div class="d-none d-md-block" style="position: relative;">
            <div style="position: absolute; right: 0; top: -180px" class="text-center bg-color-white p-4">
              <h4 class="serif">Commerciality Category Tab</h4>
              <p>
                Filtering each category gives a full and more detailed view of KPIs in that group, allowing the user to easily focus their efforts on understanding the health of the SSFA in relation to their store’s goals.
              </p>
            </div>
          </div>
          <!-- for desktop END -->
        </div>
      </div>
    </row-vertical-center>
  </div>
  <div v-else-if="section.id == 'proj1sect15'">
    <div class="row section-height" :style="fakeContainerStyle()">
      <div class="col-12 col-sm-5">
        <row-section-header>
          <span slot="title">
            Key Dashboard Functions
          </span>
        </row-section-header>
        <div class="px-5">
          <p>
            Clicking the KPI card on the dashboard brings up the KPI’s dialog box. The update function is always present at the top of the window. Each KPI’s update input method includes keyboard entry, toggles, or file upload, depending on what metrics are needed.
          </p>
        </div>
      </div>
      <div class="col-12 col-md-7">
        <row-vertical-center>
          <img class="img w-100 pl-3 pt-5 pb-5" src="@/../static/Interface-Example.png" alt="interface example">
        </row-vertical-center>
      </div>
    </div>
  </div>
  <div v-else-if="section.id == 'proj1sect16'">
    <!-- for mobile START -->
    <div class="row d-block d-md-none" :style="fakeContainerStyle()">
      <div class="col-12 text-center mb-5" v-for="keyFunction in keyFunctions">
        <img class="img w-100 pt-5 pb-5" :src="['@/../static/' + keyFunction.img]" alt="interface example">
        <div class="row">
          <div class="col-12">
            <h5 class="text-center serif p-1">{{ keyFunction.title }}</h5>
            <p class="text-center px-3">{{ keyFunction.body }}</p>
          </div>
        </div>
      </div>
    </div>
    <!-- for mobile END -->
    <!-- for desktop START -->
    <div class="d-none d-md-block">
      <row-vertical-center>
        <div class="row" :style="fakeContainerStyle()">
          <div class="col-md-4 text-center" v-for="keyFunction in keyFunctions">
            <img class="img w-100 pt-5 pb-5" :src="['@/../static/' + keyFunction.img]" alt="interface example">
            <div class="row">
              <div class="col-12">
                <h5 class="text-center serif p-1">{{ keyFunction.title }}</h5>
                <p class="text-center px-3">{{ keyFunction.body }}</p>
              </div>
            </div>
          </div>
        </div>
      </row-vertical-center>
    </div>
    <!-- for desktop END -->
  </div>
  <div v-else-if="section.id == 'proj1sect18'">
    <div v-if="isLeft(side)" class="mx-auto">
      <row-section-header>
        <span slot="title">
          Displaying the Commerciality Graph
        </span>
      </row-section-header>
      <div class="row">
        <div class="col-12">
          <p>
            When first brainstorming the interface, the goal was to display a venn diagram in order to show  how each category worked in relation to each other. However, after presenting a prototype to other SSFA managers in Sweden, feedback suggested that viewing the overall “health” of each category was much more beneficial to users.
          </p>
        </div>
      </div>
    </div>
    <div v-if="isRight(side)">
      <div class="row">
        <div class="col-10 offset-1 section-height v-align-wrapper">
          <div class="v-align">
            <async-animation :async-anim-props="{ name: 'vennAnim', delay: 0, onComplete: 'loop' }"></async-animation>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div v-else-if="section.id == 'proj1sect19'">
    <div v-if="isLeft(side)" class="mx-auto">
      <row-section-header>
        <span slot="title">
          Secondary Dashboard Functions
        </span>
      </row-section-header>
      <div class="row mb-5">
        <div class="col-12">
          <p>
            Other beneficial features to expand the dashboard concept include a way to generate reports and to share the details of each metric with other stakeholders, such as other management teams or the SSFA coworkers.
          </p>
          <p>
            Observing the original way the SSFA Team Lead shared information proved this task was cumbersome and antiquated (taking a screenshot, pasting into Microsoft Paint, saving the file, opening email, and attaching to send). By creating ways to share KPIs and print detailed reports the user can quickly deliver information to other stakeholders.
          </p>
        </div>
      </div>
    </div>
    <div v-if="isRight(side)">
      <row-vertical-center>
        <div class="row">
          <div class="col-8 offset-3 pt-5">
            <img class="img w-100" src="@/../static/REPORT.png" alt="Report">
          </div>
        </div>
        <div class="row">
          <div class="col-8 offset-1 pb-5 mb-5" style="position: relative; height: 150px;">
            <img class="img w-100" src="@/../static/Report Example-06.png" alt="Report example" style="position: absolute; top: -100px">
          </div>
        </div>
      </row-vertical-center>
      <div class="row bg-color-yellow">
        <!-- for mobile START -->
        <div class="d-block d-md-none col-12 section-height p-5">
          <img class="img w-100 pt-4" src="@/../static/SHARE.png" alt="SHARE">
        </div>
        <!-- for mobile END -->
        <!-- for desktop START -->
        <div class="d-none d-md-block col-8 offset-2 section-height p-5">
          <img class="img w-100 pt-4" src="@/../static/SHARE.png" alt="SHARE">
        </div>
        <!-- for desktop END -->
      </div>
    </div>
  </div>
  <div v-else-if="section.id == 'proj1sect20'">
    <div v-if="isLeft(side)" class="mx-auto">
      <row-section-header>
        <span slot="title">
          Concluding Thoughts &amp; Shortfalls
        </span>
      </row-section-header>
      <div class="row">
        <div class="col-12">
          <p>
            Though this project was conceptual the goal is to have it implemented as an internal tool for SSFA managers and other relevant users, and currently it is still being iterated with a goal of handing it over for actual development in the future.
          </p>
          <p>
            As the only designer working in a team of two, it’s worth noting that the limited team size can be a factor in pushing the potential of the project. However, reaching out to my peers for help and feedback with this project helped drive the scope and design at each step. I would especially like to thank Mason, UX Designer at Facebook and Eva, Product Designer at Copperleaf for their help and input.
          </p>
          <p>
            Overall this continues to be a challenging but fun project to develop my skills in the usability field!
          </p>
        </div>
      </div>
    </div>
    <div v-if="isRight(side)">
      <div class="row">
        <div class="col-10 offset-1 section-height v-align-wrapper">
          <div class="v-align">
            <img class="img w-100 pt-5 mt-5" src="@/../static/Interface-Example.png" alt="interface example">
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AffixedSectionBase from '@/components/AffixedSectionBase.js'
import RowPageHeader from '@/components/rows/PageHeader.vue'
import RowSectionHeader from '@/components/rows/SectionHeader.vue'
import RowColumnHeader from '@/components/rows/ColumnHeader.vue'
import RowVerticalCenter from '@/components/rows/VerticalCenter.vue'
import Card from '@/components/Card.vue'
import AsyncAnimation from '@/components/AsyncAnimation.vue'

export default {
  name: 'project-1-section-1',
  mixins: [AffixedSectionBase],
  components: {
    Card,
    RowPageHeader,
    RowSectionHeader,
    RowColumnHeader,
    RowVerticalCenter,
    AsyncAnimation,
  },
  data: function () {
    return {
      KPIwireframes: [
        "KPI-Layout_KPI-Wireframe-1.svg",
        "KPI-Layout_KPI-Wireframe-2.svg",
        "KPI-Layout_KPI-Wireframe-3.svg",
        "KPI-Layout_KPI-Wireframe-4.svg",
      ],
      constraints: [
        {
          title: "Can't Automatically Pull Data",
          animName: 'inputMethodsAnim',
          body: 'Because there is no way to automatically sync KPI data (as the technology not configured, and/or not in the store’s budget or timeline) ',
          bodyStrong: 'users must update some or all of the information manually.',
        },
        {
          title: "No File Saving Conventions",
          animName: 'fileSavingAnim',
          body: 'There’s no universal file-saving convention across the company, ',
          bodyStrong: 'so users will have to locate necessary information by communicating with various stakeholders in the business.',
        },
        {
          title: "Each Store Has Its Own Goals and Priorities",
          animName: 'storeGoalsAnim',
          body: 'Each store decides individually what SSFA goals fit into their business plan, ',
          bodyStrong: 'so users must be able to utilize the dashboard in a way that aligns with their store’s goal.',
        },
      ],
      firstSectionIcons: [
        {
          name: {
            text: 'IKEA (Side Project)',
            classes: ['little-small', 'strong'],
          },
          img: {
            path: '@/../static/ICONClient.svg',
            classes: ['w-50', 'mx-auto']
          },
        },
        {
          name: {
            text: '8 Weeks',
            classes: ['little-small', 'strong'],
          },
          img: {
            path: '@/../static/ICONTimeline.svg',
            classes: ['w-50', 'mx-auto']
          },
        },
        {
          name: {
            text: 'Team of 2',
            classes: ['little-small', 'strong'],
          },
          img: {
            path: '@/../static/ICONTeam.svg',
            classes: ['w-50', 'mx-auto']
          },
        },
        {
          name: {
            text: 'Desktop Application',
            classes: ['little-small', 'strong'],
          },
          img: {
            path: '@/../static/ICONProjectType.svg',
            classes: ['w-50', 'mx-auto']
          },
        },
      ],
      commercialityCards: [
        {
          name: {
            text: 'Efficiency',
            classes: ['little-small', 'strong', 'text-uppercase', 'pt-2', 'pb-2'],
          },
          img: {
            path: '@/../static/ICON-EFFICIENCY.svg',
            classes: ['w-50', 'mx-auto']
          },
          desc: {
            text: "KPIs that reflect how efficient the SSFA is running, such as time it takes to move product around the SSFA and the time it takes to unload a delivery truck.",
            classes: [],
          },
        },
        {
          name: {
            text: 'Profitability',
            classes: ['little-small', 'strong', 'text-uppercase', 'pt-2', 'pb-2'],
          },
          img: {
            path: '@/../static/ICON-PROFITABILITY.svg',
            classes: ['w-50', 'mx-auto']
          },
          desc: {
            text: "KPIs that reflect our bottom line, such as damaged products, IKEA service offers, and stock availability.",
            classes: [],
          },
        },
        {
          name: {
            text: 'Inspiration',
            classes: ['little-small', 'strong', 'text-uppercase', 'pt-2', 'pb-2'],
          },
          img: {
            path: '@/../static/ICON-INSPIRATION.svg',
            classes: ['w-50', 'mx-auto']
          },
          desc: {
            text: "KPIs that reflect our potential to inspire customers as they shop, such as furniture displays in the SSFA and the cleanliness of the department.",
            classes: [],
          },
        },
      ],
      experienceQuotes: [
        "I got close but I don’t know how to fully do it.",
        "The thing that I don’t like is that everything is a file with a weird name in excel, in a million lines in excel, and picking out which is which becomes really difficult.",
        "Searching through Bin Plan tools folder… I think I might have put it in here – Nope. Nope don’t remember that one either.",
      ],
      painPoints: [
        {
          point: "Finding the data for the KPI is difficult.",
          goal: "Make data easilly accessible or learnable",
        },
        {
          point: "Unconventional file naming",
          goal: "Clear naming of KPIs",
        },
        {
          point: "Outdated sharing process",
          goal: "Simplified process of communicating with team",
        },
        {
          point: "Data unavailable / hard to find / no access to data",
          goal: "Streamline data entry / syncing",
        },
        {
          point: "Arbitrary knowledge of performance goal(s)",
          goal: "Clear comparisons to goal (where applicable)",
        },
        {
          point: "Time consuming finding data",
          goal: "Efficient and intuitive tool",
        },
      ],
      lofiPoints: [
        "Organization of high volume of data present on the dashboard",
        "Quick references of what each KPI means",
        "Easy acces to update each KPI individually",
        "Familiarity and consistency with dashboard interfaces",
      ],
      prototypes: [
        [
          {
            title: "Prototype 1",
            body: "Update info on the back of card, click to get more info in the dialog box",
          },
          {
            title: "Perceived benefit",
            body: "Can quickly update the KPI without having to click into an extra dialog",
          },
        ],
        [
          {
            title: "Prototype 2",
            body: "See quick info / hint on the back of card, update info in the dialog box",
          },
          {
            title: "Perceived benefit",
            body: "can quickly see what the KPI means but have to click into an extra dialgue to update",
          },
        ],
      ],
      goalsAndDesign: [
        "Keep the update function as the main priority, ensuring entering relevant information is easy for the user.",
        "Provide help and guidance for the user to understand both how the metric affects the SSFA as well as where to find necessary information.",
        "Have both functions present so the user can refer easily to all elements while updating the KPI.",
      ],
      keyFunctions: [
        {
          img: "STATS.png",
          title: "Statistics Tab",
          body: "The KPI’s statistics can be found in the first tab, which displays graphical information and data. This provides a more detailed overview compared to the card on the main dashboard, which gives a small snapshot of how that particular KPI is performing.",
        },
        {
          img: "CONTACTS.png",
          title: "Key Contacts Tab",
          body: "Key contacts relevant for each KPI are available for the user. This not only allows for quick reference to communicate with coworkers, but also to help the user understand their network in context with each element in the SSFA.",
        },
        {
          img: "ABOUT.png",
          title: "About Tab",
          body: "The about tab helps the user understand what each KPI is, how it impacts the business, where to find relevant information and how to update the KPI. It serves as a reference for how to both use AND understand the KPI.",
        },
      ],
    }
  }
}
</script>

<style lang="scss" scoped>
</style>



// WEBPACK FOOTER //
// src/components/sections/projects/project1/Section1.vue